import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cx } from '@parrot/utils';
import { Spinner } from '../Spinner';
import styles from './Button.module.css';

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  /** Visual role. `primary` is the main CTA — only one per view. */
  variant?: 'primary' | 'secondary' | 'ghost' | 'destructive' | 'brand' | 'glass';
  /** `sm` for dense UI (tables), `md` everywhere, `lg` for hero emphasis. */
  size?: 'sm' | 'md' | 'lg';
  /** Shows a spinner and blocks further presses. Reword the label for the wait. */
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
};

/**
 * The standard action trigger. A real `<button>`, so keyboard and focus behaviour
 * come for free; every native button prop passes through.
 *
 * `type` defaults to `'button'` — inside a form, pass `type={'submit'}` explicitly
 * rather than relying on the HTML default, which submits by accident.
 *
 * An icon with no children makes it square; there is no separate IconButton. Give
 * those an `aria-label` — it is what `getByRole('button', { name })` matches in
 * tests, so without one they are awkward to select.
 */
export const Button = ({
  variant = 'primary',
  size = 'md',
  type = 'button',
  loading = false,
  iconLeft,
  iconRight,
  disabled,
  className,
  children,
  ...buttonProps
}: Props) => {
  const iconOnly = children === undefined || children === null || children === false;

  return (
    <button
      type={type}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      className={cx(
        styles.base,
        styles[variant],
        styles[size],
        loading && styles.loading,
        iconOnly && styles.iconOnly,
        className,
      )}
      {...buttonProps}
    >
      {loading && <Spinner size={'xs'} aria-hidden={'true'} />}
      {iconLeft !== undefined && (
        <span className={styles.icon} aria-hidden={'true'}>
          {iconLeft}
        </span>
      )}
      {children}
      {iconRight !== undefined && (
        <span className={styles.icon} aria-hidden={'true'}>
          {iconRight}
        </span>
      )}
    </button>
  );
};
